import {
  camelCase,
  groupBy,
  map,
  trim,
  result,
  zipObject
} from 'libraries/lodash'
import { toJson } from 'libraries/front-end-core/helpers/xml'
import linkifyHtml from 'linkify-html'
import dayjs from 'dayjs'
import { DATE_FORMAT, DATE_WITH_LONG_MONTH } from 'constants/locale'
import { parseJson, sortKeys } from 'helpers/transforms'
import { Article, ArticleResponse, CAAccordionContent, CASearchResult, Folder, Guide, ParsedFolder } from 'interfaces/ca'
import { toFacet } from 'requests/qa/transforms'

// FIX ME remove this function once BE updates the homepage articles endpoint (api does not return mdx content)
export const toHomePageArticles = function (data: unknown): Record<string, CAAccordionContent[]> {
  const toContentSection = function (cs: { childNodes: [], attributes: { date: string, id: number, imageUrl: string } }, i: number): CAAccordionContent {
    const {
      title,
      text = '',
      barristersComment = null,
      barristersHeading = null,
      fcContent = null,
      position = null,
      trimmed = null
    } = zipObject(
      map(cs.childNodes, 'name').map(camelCase),
      map(cs.childNodes, 'content').map(trim)
    )
    const contentText = linkifyHtml(
      text.replace(
        /FC Content:(.*)/g,
        '<div class="ca-fc-content"><strong>FC Content:</strong> $1</div>'
      )
    )

    const content = []
    const barristersBox = (barristersHeading !== null && barristersHeading !== '') ? barristersHeading : barristersComment
    const hasBarristersBox = barristersBox !== null && barristersBox !== ''
    const hasBarristersHeading = barristersHeading !== null && barristersHeading !== ''
    const hasBarristersComment = barristersComment !== null && barristersComment !== ''
    const hasFcContent = fcContent !== null && fcContent !== ''

    hasBarristersBox && content.push('<div class="ca-barristers-comment">')
    hasBarristersHeading &&
      content.push(`<div class="heading">${barristersHeading}</div>`)
    hasBarristersComment &&
      content.push(`<div class="body">${barristersComment}</div>`)
    hasBarristersBox && content.push('</div>')
    contentText !== '' && content.push(`<div class="ca-content">${contentText}</div>`)
    hasFcContent && content.push(`<div class="ca-fc-content">${fcContent}</div>`)

    return {
      content: content.join(''),
      date: dayjs(cs.attributes.date).format(DATE_FORMAT),
      id: cs.attributes.id !== 0 ? cs.attributes.id : Math.ceil(Math.random() * 10000000).toString(16),
      number: String(i + 1),
      title,
      position,
      trimmed,
      imageUrl: cs.attributes.imageUrl
    }
  }

  return sortKeys(
    groupBy(
      result(
        toJson(data, [
          'ContentSections',
          'ContentSection',
          'title',
          'Text',
          'barristers-comment',
          'barristers-heading',
          'fc-content',
          'position',
          'trimmed'
        ]),
        '0.childNodes',
        []
      ).map(toContentSection),
      'date'
    )
  )
}

export const toCurrentAwareness = function (data: ArticleResponse): Record<string, CAAccordionContent[]> {
  const toContentSection = (cs: { childNodes: [], attributes: { date: string, id: number, imageUrl: string } }, i: number): CAAccordionContent => {
    const {
      title,
      text = '',
      barristersComment = null,
      barristersHeading = null,
      fcContent = null,
      position = null,
      trimmed = null
    } = zipObject(
      map(cs.childNodes, 'name').map(camelCase),
      map(cs.childNodes, 'content').map(trim)
    )
    const contentText = linkifyHtml(
      text.replace(
        /FC Content:(.*)/g,
        '<div class="ca-fc-content"><strong>FC Content:</strong> $1</div>'
      )
    )

    const content = []
    const barristersBox = (barristersHeading !== null && barristersHeading !== '') ? barristersHeading : barristersComment
    const hasBarristersBox = barristersBox !== null && barristersBox !== ''
    const hasBarristersHeading = barristersHeading !== null && barristersHeading !== ''
    const hasBarristersComment = barristersComment !== null && barristersComment !== ''
    const hasFcContent = fcContent !== null && fcContent !== ''

    hasBarristersBox && content.push('<div class="ca-barristers-comment">')
    hasBarristersHeading &&
      content.push(`<div class="heading">${barristersHeading}</div>`)
    hasBarristersComment &&
      content.push(`<div class="body">${barristersComment}</div>`)
    hasBarristersBox && content.push('</div>')
    contentText !== '' && content.push(`<div class="ca-content">${contentText}</div>`)
    hasFcContent && content.push(`<div class="ca-fc-content">${fcContent}</div>`)

    return {
      content: content.join(''),
      date: dayjs(cs.attributes.date).format(DATE_FORMAT),
      id: cs.attributes.id !== 0 ? cs.attributes.id : Math.ceil(Math.random() * 10000000).toString(16),
      number: String(i + 1),
      title,
      position,
      trimmed,
      imageUrl: cs.attributes.imageUrl
    }
  }

  const { articles = [] } = parseJson(data)
  return sortKeys(groupBy(articles.map((article: Article, index: number) => {
    const parsedData =
      toJson(article.content, [
        'ContentSections',
        'ContentSection',
        'title',
        'Text',
        'barristers-comment',
        'barristers-heading',
        'fc-content',
        'position',
        'trimmed'
      ])

    return toContentSection(parsedData[0] as any, index)
  }), 'date'))
}

export const toGuides = function (_data: object): { guides: number[], guideList: Record<string, Guide> } {
  const data: Guide[] = parseJson(_data)
  const guideList: Record<string, Guide> = {}
  const guides: number[] = []
  data.forEach(item => {
    const id = Math.floor(Math.random() * 10000)
    const datePublished = dayjs(dayjs(item.datePublished, DATE_WITH_LONG_MONTH)).format(DATE_FORMAT)
    const dateLastUpdated = dayjs(dayjs(item.dateLastUpdated, DATE_WITH_LONG_MONTH)).format(
      DATE_FORMAT
    )
    const link = item.isPublic ? '../static/' + item.fileName : 'static/' + item.fileName
    guideList[id] = Object.assign({}, item, {
      datePublished,
      dateLastUpdated,
      link,
      id
    })
    guides.push(id)
  })
  return {
    guides,
    guideList
  }
}

export const toContentFolders = function (_data: object): { folders: Record<string, ParsedFolder>, rootLevel: ParsedFolder[] } {
  const data: Folder[] = parseJson(_data)

  const folders: Record<string, ParsedFolder> = {}
  const rootLevel: ParsedFolder[] = []

  const analyzeFolder = (path: string[], depth = 0) => ({ children, documents, name, uuid }: Folder) => {
    folders[uuid] = {
      uuid,
      name,
      documents,
      documentCount: documents.length,
      children: () => children.map(({ uuid }) => folders[uuid]),
      path: [...path, uuid],
      depth
    }

    path.forEach(ancestorUuid => { folders[ancestorUuid].documentCount += documents.length })
    children.forEach(analyzeFolder([...path, uuid], depth + 1))
    ;(path.length === 0) && rootLevel.push(folders[uuid])
  }
  data.forEach(analyzeFolder([]))

  return { folders, rootLevel }
}

export const toCurrentAwarenessSearchResults = function (data: object): {
  resultCount: number
  results: Array<{
    id: string
    title: string | null
    content: string[]
    dateOfPublication: string
    owningAreaOfLaw: string | undefined
    areaOfLaw: string[] | undefined
  }>
  facets: Array<ReturnType<typeof toFacet>>
} {
  const parsedData: CASearchResult = parseJson(data).data

  return {
    resultCount: parsedData.total,
    results: parsedData.articles.map(r => ({
      id: r.id,
      title: r.title !== '' ? r.title : null,
      content: r.snippets,
      dateOfPublication: r.dateOfPublication,
      owningAreaOfLaw: r.owningAreaOfLaw,
      areaOfLaw: r.areaOfLaw
    })),
    facets: map(parsedData.facets, toFacet)
  }
}

export const toPublishedCaKeyPicks = (data: object): unknown => parseJson(data)
